import React from "react";
import {Button, message, Table, Tooltip} from "antd";
import {sumBy} from "lodash";
import {StringBuilder} from "../../AppUtils.js";
import {ProductUpgradeOptionsAmountType} from "../../consts.js";
import {OrderStatus, statusToTextAndColor} from "../../components/index.jsx";
import {isOrderHasShippingProvider} from "../utils.jsx";
import {
    generateLionWheelOrderDeliveryCertificateLink,
    generateLionWheelOrderDeliveryStatusLink,
    getOrderStepStatus
} from "../OrderDeliveryStatus/utils.jsx";
import moment from "moment";
import {declineOrderOptionsToText} from "../DeclineOrderModal/consts.js";
import {dateTimeRange} from "../../utils/DateUtils.jsx";
import {kosherOptions} from "../../admin/upload/AdminUploadUtils.jsx";
import {openWindow} from "../../http/WindowOpener.jsx";
import {createUrlWithoutTransformations} from "../../utils/cloudinaryUtils.jsx";
import classnames from "classnames";
import {NOOP} from "../../utils/NOOP.jsx";
import {PriceTypes} from "../../admin/upload/PriceSelectorHelper.jsx";
import {SubProductTitleContainer} from "../../marketplace/OrderFormModularProductsPanel/RenderSubProductsList.jsx";
import {DeliverySystem} from "../../admin/AdminDeliveryProviders/consts.js";
import {FoodRequestsShortIdToName} from "../../marketplace/ProductsFilterModal/consts.jsx";
import {FoodRequestToIcon} from "../../marketplace/OrderFormModularProductsPanel/consts.jsx";

export const generateTableContent = eventInfo => {
    if (eventInfo.isVoucherOrder) {
        return {
            dataSource: [],
            columns: [],
            summary: () => <></>
        };
    }

    const isModularOrder =
        eventInfo?.chosenPriceType === PriceTypes.ByModular && eventInfo?.productAmountType === "חבילה";

    const amountTitle = new StringBuilder("כמות")
        .append(isModularOrder ? "פריטים" : eventInfo.productAmountType)
        .toString();

    const priceTitle = isModularOrder ? "מחיר" : `מחיר ל${eventInfo.productAmountType}`;

    const columns = [
        {
            title: "תיאור",
            dataIndex: "desc"
        },
        {
            title: amountTitle,
            className: "amount",
            dataIndex: "amount"
        },
        ...(isModularOrder
            ? [
                  {
                      title: priceTitle,
                      dataIndex: "pricePerUnit"
                  },
                  {
                      title: "סה״כ",
                      dataIndex: "totalPrice"
                  }
              ]
            : [])
    ];

    const dataSource = eventInfo?.modularProducts?.length
        ? eventInfo.modularProducts.map((subProduct, index) => ({
              key: `subproduct-${index}`,
              desc: (
                  <>
                      <SubProductTitleContainer subProduct={subProduct} />
                      <div>{subProduct?.description}</div>
                      {!!subProduct.catalogNumber && (
                          <div>{new StringBuilder().append(`מק"ט:`).append(subProduct.catalogNumber).toString()}</div>
                      )}
                  </>
              ),
              amount: subProduct.amount,
              pricePerUnit: new StringBuilder().append(subProduct.price).append("₪").toString(),
              totalPrice: new StringBuilder()
                  .append(subProduct?.price * subProduct.amount)
                  .append("₪")
                  .toString()
          }))
        : [
              {
                  key: "main-product",
                  desc: (
                      <>
                          <span>
                              <b>{eventInfo.orderName ?? eventInfo.productName}</b>
                          </span>
                          {eventInfo.productPackageInfo ? (
                              <div className="order-information-card-details-table-desc">
                                  {eventInfo.productPackageInfo}
                              </div>
                          ) : (
                              <div
                                  className="order-information-card-details-table-desc"
                                  dangerouslySetInnerHTML={{__html: eventInfo.productDescription}}
                              />
                          )}
                      </>
                  ),
                  amount: eventInfo.productAmount
              }
          ];

    if (eventInfo.upgradeOptions?.options?.length && isModularOrder) {
        for (const [index, upgradeOption] of eventInfo.upgradeOptions.options.entries()) {
            const amount = upgradeOption.type === ProductUpgradeOptionsAmountType.byUnit ? upgradeOption.amount : 1;
            dataSource.push({
                key: `upgradeOption-${index}`,
                desc: (
                    <div>
                        <div className="order-information-card-details-table-title">
                            {new StringBuilder().append("פריט נוסף - ").append(upgradeOption?.name).toString()}
                        </div>
                        <div className="order-information-card-details-table-desc">{upgradeOption?.description}</div>
                    </div>
                ),
                amount,
                pricePerUnit: new StringBuilder().append(`${upgradeOption.price}`).append("₪").toString(),
                totalPrice: new StringBuilder()
                    .append(amount * upgradeOption.price)
                    .append("₪")
                    .toString()
            });
        }
    }

    if (eventInfo?.upgradeOptions?.files?.length) {
        dataSource.push({
            key: "upgrade-files",
            desc: (
                <div>
                    <div className="order-information-card-details-table-title">
                        {new StringBuilder().append("קבצים רלוונטים (לוגו, גרפיקות וכו׳)").toString()}
                    </div>
                    <div
                        className={classnames(
                            "order-information-card-details-table-desc",
                            "order-information-card-details-table-link"
                        )}
                        onClick={() =>
                            openWindow(createUrlWithoutTransformations(eventInfo?.upgradeOptions?.files?.[0]?.fileUrl))
                        }>
                        {"פתיחת קבצים רלוונטים"}
                    </div>
                </div>
            ),
            amount: eventInfo?.upgradeOptions?.files?.length,
            pricePerUnit: "",
            totalPrice: ""
        });
    }

    return {
        summary: () => {
            if (!isModularOrder) {
                return (
                    <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>סה״כ</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>{eventInfo?.productAmount}</Table.Summary.Cell>
                    </Table.Summary.Row>
                );
            }

            const totalAmount =
                sumBy(eventInfo.modularProducts, subProduct => subProduct.amount) +
                (eventInfo.upgradeOptions?.options?.length ?? 0) +
                (eventInfo?.upgradeOptions?.files?.length ?? 0);

            const totalModularProducts = sumBy(
                eventInfo.modularProducts,
                subProduct => subProduct?.price * subProduct.amount
            );

            const totalUpgradePrice = sumBy(eventInfo.upgradeOptions?.options, upgradeOption => {
                const amount = upgradeOption.type === ProductUpgradeOptionsAmountType.byUnit ? upgradeOption.amount : 1;
                return amount * upgradeOption.price;
            });

            const totalPrice = totalModularProducts + totalUpgradePrice;

            return (
                <>
                    <Table.Summary.Row className="">
                        <Table.Summary.Cell index={0}>{"סה״כ כולל מע״מ"}</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>{totalAmount}</Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>{""}</Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>
                            {new StringBuilder().append(totalPrice.toFixed(2)).append("₪").toString()}
                        </Table.Summary.Cell>
                    </Table.Summary.Row>
                </>
            );
        },
        dataSource,
        columns
    };
};

export const getOrderViewStatusContentForRegularShippingProvider = order => {
    if (!order) {
        return [];
    }

    const {isOrderReviewedByManager, isOrderAccepted, isOrderDelivered} = getOrderStepStatus(order);

    return [
        {
            id: "pending for manager approve",
            title: statusToTextAndColor?.[order?.status]?.[0],
            subTitle: "ההזמנה ממתינה לאישור מנהל, לאחר האישור היא תעבור לבית העסק",
            isStepComplete: !!order?.needManagerConfirm,
            shouldHide: !order?.needManagerConfirm
        },
        {
            id: "manager approve comments",
            title: statusToTextAndColor?.[order?.status]?.[0],
            subTitle: "סיבת סירוב המנהל:",
            description: [order?.cancelReason || "-"],
            isStepComplete: !!isOrderReviewedByManager,
            shouldHide: !order?.needManagerConfirm
        },
        {
            id: "order sent to provider approve",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "yellow",
            backgroundColor: "yellow",
            description: [
                new StringBuilder()
                    .append("ברגעים אלה צוות")
                    .append(order.providerBrand)
                    .append("עובר על הזמנתך")
                    .toString()
            ],
            endTime: moment(order.createdAt).add("4", "hour"),
            isStepComplete: order?.status === OrderStatus.pending
        },
        {
            id: "order canceled",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "gray",
            backgroundColor: "gray",
            isStepComplete: order?.status === OrderStatus.cancelled
        },
        {
            id: "order declined",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "red",
            subTitle: "סיבת סירוב:",
            description: [declineOrderOptionsToText?.[order?.declineReason], order.declineComment].filter(Boolean),
            backgroundColor: "red",
            isStepComplete: order?.status === OrderStatus.declined && !isOrderReviewedByManager
        },
        {
            id: "provider approve",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "blue",
            backgroundColor: "blue",
            description: [
                new StringBuilder()
                    .append("הזמנה שביצעת אושרה על ידי")
                    .append(order?.providerBrand || "הספק")
                    .append("איזה כיף! 🤩")
                    .toString()
            ],
            isStepComplete: order.status === OrderStatus.accepted,
            actions: [
                {
                    text: "לבירור איפה ההזמנה מול בית העסק",
                    onClick: () => {
                        message.error("ביום האספקה ניתן יהיה לדבר ישירות עם בית העסק לבדיקת סטטוס האספקה.");
                    },
                    color: "gray"
                }
            ]
        },
        {
            id: "provider approve and start of dtStart",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "blue",
            backgroundColor: "blue",
            description: [
                new StringBuilder().append("לבירור איפה ההזמנה מול בית העסק ניתן להתקשר לטלפון:").toString(),
                <a key="provider-phone" href={`tel:${order?.providerPhone}`}>
                    {order?.providerPhone}
                </a>
            ],
            isStepComplete: !!(isOrderAccepted && moment().isAfter(order.dtstart)),
            shouldHide: !!isOrderHasShippingProvider(order)
        },
        {
            id: "order arrived to destination",
            title: statusToTextAndColor?.[OrderStatus.delivered]?.[0],
            description: ["ההזמנה הגיעה ליעד"],
            isStepComplete: !!isOrderDelivered,
            titleColor: "green",
            backgroundColor: "green"
        }
    ].filter(orderStep => !orderStep.shouldHide && orderStep.isStepComplete);
};

export const getOrderViewStatusContentForShippingProvider = order => {
    if (!order) {
        return [];
    }

    const durationInTrafficSeconds = order?.driverLocationData?.durationInTrafficValue;
    const estimatedArrivalTime =
        typeof durationInTrafficSeconds === "number" ? Date.now() + durationInTrafficSeconds * 1000 : null;
    const {isOrderReviewedByManager, isOrderDelivered, isOrderOnTheWay} = getOrderStepStatus(order);

    return [
        {
            id: "pending for manager approve",
            title: statusToTextAndColor?.[order?.status]?.[0],
            subTitle: "ההזמנה ממתינה לאישור מנהל, לאחר האישור היא תעבור לבית העסק",
            isStepComplete: !!order?.needManagerConfirm,
            shouldHide: !order?.needManagerConfirm
        },
        {
            id: "manager approve comments",
            title: statusToTextAndColor?.[order?.status]?.[0],
            subTitle: "סיבת סירוב המנהל:",
            description: [order?.cancelReason || "-"],
            isStepComplete: !!isOrderReviewedByManager,
            shouldHide: !order?.needManagerConfirm
        },
        {
            id: "order sent to provider approve",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "yellow",
            backgroundColor: "yellow",
            description: [
                new StringBuilder()
                    .append("ברגעים אלה צוות")
                    .append(order.providerBrand)
                    .append("עובר על הזמנתך")
                    .toString()
            ],
            endTime: moment(order.createdAt).add("4", "hour"),
            isStepComplete: order?.status === OrderStatus.pending
        },
        {
            id: "order canceled",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "gray",
            backgroundColor: "gray",
            isStepComplete: order?.status === OrderStatus.cancelled
        },
        {
            id: "order declined",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "red",
            subTitle: "סיבת סירוב:",
            description: [declineOrderOptionsToText?.[order?.declineReason], order.declineComment].filter(Boolean),
            backgroundColor: "red",
            isStepComplete: order.needManagerConfirm
                ? order?.status === OrderStatus.declined && isOrderReviewedByManager
                : order?.status === OrderStatus.declined
        },
        {
            id: "provider approve",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "blue",
            backgroundColor: "blue",
            description: [
                new StringBuilder()
                    .append("הזמנה שביצעת אושרה על ידי")
                    .append(order?.providerBrand || "הספק")
                    .append("איזה כיף! 🤩")
                    .toString(),
                ...(order?.driver
                    ? [
                          <br key="driver-info-br" />,
                          <div key="driver-info">
                              <p key="driver-title">
                                  <strong>פרטי השליח</strong>
                              </p>
                              <span>
                                  {order?.driver?.name} | {order?.driver?.phone}
                                  {order?.driver?.idNumber && <> | {order.driver.idNumber}</>}
                                  {order?.driver?.licenseNumber && <> | מס׳ רכב: {order.driver.licenseNumber}</>}
                              </span>
                          </div>
                      ]
                    : [])
            ].filter(Boolean),
            isStepComplete: order.status === OrderStatus.accepted,
            actions: [
                ...(!order?.driver
                    ? [
                          {
                              text: "הצגת פרטי השליח ומעקב ההזמנה",
                              onClick: () => {
                                  message.error("ביום האספקה נשלח לך הודעה שההזמנה נאספה עם לינק מעקב ופרטי השליח.");
                              },
                              color: "gray"
                          }
                      ]
                    : [
                          {
                              text: `לשיחה עם השליח`,
                              onClick: () => {
                                  const phone = order?.driver?.phone;
                                  if (phone) {
                                      window.location.href = `tel:${phone}`;
                                  }
                              }
                          }
                      ])
            ]
        },
        {
            id: "pickup by delivery provider status",
            titleColor: "blue",
            backgroundColor: "blue",
            description: [
                <div key="order-on-the-way-tracker" className="order-on-the-way-tracker">
                    <p>היי {order.companyContact} 👋</p>
                    <p>השליח כבר בדרך אלייך וצפוי להגיע בשעה</p>
                    {estimatedArrivalTime && (
                        <div className="order-on-the-way-tracker-timer">
                            <span className="line"></span>
                            <span className="time">{moment(estimatedArrivalTime).format("HH:mm")}</span>
                            <span className="line"></span>
                        </div>
                    )}
                    <div>
                        <p>
                            <strong>פרטי השליח</strong>
                        </p>
                        <span>
                            {order?.driver?.name} | {order?.driver?.idNumber} | מס׳ רכב: {order?.driver?.licenseNumber}
                        </span>
                    </div>
                </div>
            ],
            actions: [
                {
                    text: `לשיחה עם השליח`,
                    onClick: () => {
                        const phone = order?.driver?.phone;
                        if (phone) {
                            window.location.href = `tel:${phone}`;
                        }
                    }
                }
            ],
            isStepComplete: isOrderOnTheWay,
            shouldHide: order?.deliverySystem !== DeliverySystem.WellB
        },
        {
            id: "pickup by delivery provider status",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "blue",
            backgroundColor: "blue",
            description: ["ההזמנה יצאה לדרך! ניתן לראות איפה ההזמנה נמצאת בכל רגע"],
            actions: [
                {
                    text: "הצגת פרטי השליח ומעקב ההזמנה",
                    onClick: () => {
                        window.open(generateLionWheelOrderDeliveryStatusLink(order?.shippingDetails), "_blank");
                    }
                }
            ],
            isStepComplete: isOrderOnTheWay,
            shouldHide: order?.deliverySystem === DeliverySystem.WellB
        },
        {
            id: "order arrived to destination",
            title: statusToTextAndColor?.[OrderStatus.delivered]?.[0],
            description: !order?.deliveryLeftAtDestination
                ? ["ההזמנה הגיעה ליעד"]
                : [
                      <p key="delivery-left-at-destination-description">
                          השליח הגיע לכתובת, אך לא היה מענה בעת המסירה. ההזמנה הושארה בכתובת המצויינת.
                      </p>,
                      <Button
                          key="show-delivery-left-at-destination-signature"
                          type="link"
                          onClick={() => openWindow(order?.clientSignature?.signatureUrl, "_blank", true)}>
                          הצגת תמונת מסירה
                      </Button>
                  ],
            isStepComplete: !!isOrderDelivered,
            titleColor: "green",
            backgroundColor: "green"
        }
    ].filter(orderStep => !orderStep.shouldHide && orderStep.isStepComplete);
};

export const getProviderOrderViewStatusContentForRegularShippingProvider = (
    order,
    onDelivered,
    isLoadingOnDelivered
) => {
    if (!order) {
        return [];
    }

    const {isOrderDelivered, isOrderWaitingForDeliveredFeedback} = getOrderStepStatus(order);

    return [
        {
            id: "order sent to provider approve",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "yellow",
            backgroundColor: "yellow",
            description: ["יש לעבור על פרטי ההזמנה ולסמן האם תוכלו לבצע אותה"],
            endTime: moment(order.createdAt).add("4", "hour"),
            isStepComplete: order?.status === OrderStatus.pending
        },
        {
            id: "order canceled",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "gray",
            backgroundColor: "gray",
            isStepComplete: order?.status === OrderStatus.cancelled
        },
        {
            id: "order declined",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "red",
            subTitle: "סיבת סירוב:",
            description: [declineOrderOptionsToText?.[order?.declineReason], order.declineComment].filter(Boolean),
            backgroundColor: "red",
            isStepComplete: order?.status === OrderStatus.declined
        },
        {
            id: "provider approve",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "blue",
            backgroundColor: "blue",
            description: ["יש לשריין ביומן את הכתובת ומועד האספקה"],
            isStepComplete: order.status === OrderStatus.accepted
        },
        {
            id: "order waiting for delivered feedback",
            title: "מועד האספקה חלף, האם ההזמנה סופקה",
            titleColor: "blue",
            backgroundColor: "blue",
            actions: [
                {
                    text: "ההזמנה הגיע ליעד",
                    onClick: onDelivered,
                    loading: isLoadingOnDelivered
                }
            ],
            isStepComplete: isOrderWaitingForDeliveredFeedback
        },
        {
            id: "order arrived to destination",
            title: statusToTextAndColor?.[OrderStatus.delivered]?.[0],
            description: ["ההזמנה הגיעה ליעד"],
            titleColor: "green",
            backgroundColor: "green",
            isStepComplete: !!isOrderDelivered
        }
    ].filter(orderStep => !orderStep.shouldHide && orderStep.isStepComplete);
};

export const getProviderOrderViewStatusContentForShippingProvider = order => {
    if (!order) {
        return [];
    }

    const {isOrderDelivered, isOrderOnTheWay} = getOrderStepStatus(order);

    return [
        {
            id: "order sent to provider approve",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "yellow",
            backgroundColor: "yellow",
            description: ["יש לעבור על פרטי ההזמנה ולסמן האם תוכלו לבצע אותה"],
            endTime: moment(order.createdAt).add("4", "hour"),
            isStepComplete: order?.status === OrderStatus.pending
        },
        {
            id: "order canceled",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "gray",
            backgroundColor: "gray",
            isStepComplete: order?.status === OrderStatus.cancelled
        },
        {
            id: "order declined",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "red",
            subTitle: "סיבת סירוב:",
            description: [declineOrderOptionsToText?.[order?.declineReason], order.declineComment].filter(Boolean),
            backgroundColor: "red",
            isStepComplete: order?.status === OrderStatus.declined
        },
        {
            id: "provider approve",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "blue",
            backgroundColor: "blue",
            description: [
                "יש לשריין ביומן את מועד האספקה. הזמנה זו תאסף על ידי שליח well-b",
                order?.deliverySystem === DeliverySystem.Lionwheel && (
                    <a
                        className="order-information-card-open-link"
                        key="delivery-certificate"
                        onClick={() =>
                            window.open(generateLionWheelOrderDeliveryCertificateLink(order?.shippingDetails), "_blank")
                        }>
                        להדפסת תעודת משלוח
                    </a>
                )
            ],
            actions: [
                {
                    text: "📞 ליצירת קשר עם השליח",
                    onClick: () => {
                        if (order?.deliverySystem === DeliverySystem.Lionwheel) {
                            window.open(generateLionWheelOrderDeliveryStatusLink(order?.shippingDetails), "_blank");
                        } else {
                            const phone = order?.driver?.phone;
                            if (phone) {
                                window.location.href = `tel:${phone}`;
                            }
                        }
                    },
                    disabled: !order?.driver?.phone
                }
            ],
            isStepComplete: order.status === OrderStatus.accepted
        },
        {
            id: "pickup by delivery provider status",
            title: statusToTextAndColor?.[order?.status]?.[0],
            titleColor: "blue",
            backgroundColor: "blue",
            description: ["ההזמנה נאספה מבית העסק"],
            isStepComplete: isOrderOnTheWay
        },
        {
            id: "order arrived to destination",
            title: statusToTextAndColor?.[OrderStatus.delivered]?.[0],
            description: !order?.deliveryLeftAtDestination
                ? ["ההזמנה הגיעה ליעד"]
                : [
                      <p key="delivery-left-at-destination-description">
                          השליח הגיע לכתובת, אך לא הי�ה מענה בעת המסירה. ההזמנה הושארה בכתובת המצויינת.
                      </p>,
                      <Button
                          key="show-delivery-left-at-destination-signature"
                          type="link"
                          onClick={() => openWindow(order?.clientSignature?.signatureUrl, "_blank", true)}>
                          הצגת תמונת מסירה
                      </Button>
                  ],
            titleColor: "green",
            backgroundColor: "green",
            isStepComplete: !!isOrderDelivered
        }
    ].filter(orderStep => !orderStep.shouldHide && orderStep.isStepComplete);
};

export const generateOrderViewStatusContent = order => {
    if (!order) {
        return [];
    }

    if (isOrderHasShippingProvider(order)) {
        return getOrderViewStatusContentForShippingProvider(order);
    }

    return getOrderViewStatusContentForRegularShippingProvider(order);
};

export const generateProviderOrderViewStatusContent = (order, onDelivered = NOOP, isLoadingOnDelivered) => {
    if (!order) {
        return [];
    }

    const {isOrderSentForProviderApproval} = getOrderStepStatus(order);

    if (!isOrderSentForProviderApproval) {
        return [];
    }

    if (isOrderHasShippingProvider(order)) {
        return getProviderOrderViewStatusContentForShippingProvider(order);
    }

    return getProviderOrderViewStatusContentForRegularShippingProvider(order, onDelivered, isLoadingOnDelivered);
};

export const getWhenSectionTitle = eventInfo => {
    const whenSection = dateTimeRange(eventInfo.dtstart, eventInfo.until);

    const whenSectionStringBuilder = new StringBuilder()
        .append(whenSection.dtStartDateFormat)
        .append("בשעה")
        .append(whenSection.dtStartHourFormat);

    if (eventInfo.until && whenSection.untilDateFormat) {
        whenSectionStringBuilder.append("עד");
        whenSectionStringBuilder.append(whenSection.untilDateFormat);
    }

    if (eventInfo.until && whenSection.untilHourFormat) {
        whenSectionStringBuilder.append("עד השעה");
        whenSectionStringBuilder.append(whenSection.untilHourFormat);
        whenSectionStringBuilder.append("(טווח של");
        whenSectionStringBuilder.append(`${whenSection.durationFormat})`);
    }

    return whenSectionStringBuilder.toString();
};

export const getWhenSectionTitleForBrief = eventInfo => {
    const whenSection = dateTimeRange(eventInfo.dtstart, eventInfo.until);

    const whenSectionStringBuilder = new StringBuilder()
        .append(whenSection.dtStartDateFormat)
        .append("בשעה")
        .append(whenSection.dtStartHourFormat);

    if (eventInfo.until && whenSection.untilDateFormat) {
        whenSectionStringBuilder.append("עד");
        whenSectionStringBuilder.append(whenSection.untilDateFormat);
    }

    if (eventInfo.until && whenSection.untilHourFormat) {
        whenSectionStringBuilder.append("-");
        whenSectionStringBuilder.append(whenSection.untilHourFormat);
    }

    return whenSectionStringBuilder.toString();
};

export const shouldShowKosherCertificate = eventInfo =>
    eventInfo?.kosherCertificateImageUrl &&
    (eventInfo?.kosher === kosherOptions[0] || eventInfo?.kosher === kosherOptions[2]);

export const generateCompanyRequests = companyRequests => {
    const companyRequestsStringBuilder = new StringBuilder(null, ", ");

    for (const req of companyRequests ?? []) {
        const companyRequestStringBuilder = new StringBuilder();
        if (req?.title) {
            companyRequestStringBuilder.append(req.title);
            companyRequestStringBuilder.append(req?.amount ? `(${req.amount})` : "");
        } else {
            companyRequestStringBuilder.append(req);
        }

        companyRequestsStringBuilder.append(companyRequestStringBuilder.toString());
    }

    return companyRequestsStringBuilder.toString();
};

export const generateUpgradeOptions = upgradeOptions => {
    return (upgradeOptions?.options ?? []).map((upgradeOption, optionIndex) => {
        const upgradeOptionStringBuilder = new StringBuilder();
        if (upgradeOption?.amount) {
            upgradeOptionStringBuilder.append(upgradeOption?.amount);
            upgradeOptionStringBuilder.append("-");
        }
        upgradeOptionStringBuilder.append(upgradeOption?.name);
        upgradeOptionStringBuilder.append("|");
        upgradeOptionStringBuilder.append(`${upgradeOption?.price * (upgradeOption?.amount ?? 1)}₪`);

        return (
            <div key={optionIndex} style={{display: "flex", alignItems: "center", marginBottom: 4}}>
                <span>{upgradeOptionStringBuilder.toString()}</span>
                {(upgradeOption?.foodRequests ?? []).map((foodOption, index) => (
                    <Tooltip key={index} title={FoodRequestsShortIdToName[foodOption]}>
                        <img
                            className="sub-product-food-request-image"
                            src={FoodRequestToIcon[foodOption]}
                            alt={foodOption}
                            style={{marginRight: 5, marginLeft: 5}}
                        />
                    </Tooltip>
                ))}
            </div>
        );
    });
};

export const shouldShowCancelReason = canceledEvent =>
    canceledEvent?.status === OrderStatus.cancelled &&
    canceledEvent?.cancelReason &&
    (canceledEvent?.eventStatusMap ?? []).some(({id}) => id === OrderStatus.canceledByManager);
